import ForceGraph3D, { ConfigOptions, ForceGraph3DInstance, NodeObject } from '3d-force-graph';
import * as THREE from 'three';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { Galaxy } from '../models/galaxy';
import { GalaxyNode, GalaxyNodeType } from '../models/galaxy-node';
import { GalaxyNodeRenderer } from './galaxy-node-renderer';
import { EventEmitter } from '@angular/core';

import { addSpiralStarfieldLOD, addStarfieldBackground } from './galaxy-starfield';

export class GalaxyRenderer {
  private graph: ForceGraph3DInstance;
  private galaxy: Galaxy;
  private backgroundColor: string = '#000000';
  private selectedNode: GalaxyNode | null = null;
  private selectedDescendants: Set<GalaxyNode> = new Set();
  private bloomPass: UnrealBloomPass | null = null;

  public nodeSelected = new EventEmitter<GalaxyNode | null>();

  constructor(container: HTMLElement, galaxy: Galaxy) {
    this.galaxy = galaxy;
    this.initGraph(container);
    this.initControls();
    this.addStarfield();
    this.initBloomRenderer();
  }

  private initGraph(container: HTMLElement) {
    const configOptions: ConfigOptions = {
      controlType: 'orbit',
      rendererConfig: { antialias: true, alpha: true },
    };
    this.graph = new ForceGraph3D(container, configOptions);
    this.graph.width(container.offsetWidth);
    this.graph.height(container.offsetHeight);
    this.graph.backgroundColor(this.backgroundColor);
    this.graph.enableNodeDrag(false);
    this.graph.graphData(this.galaxy.galaxyData);

    this.graph.numDimensions(2);

    this.graph.nodeAutoColorBy(GalaxyNodeRenderer.nodeAutoColorBy);
    this.graph.nodeLabel(GalaxyNodeRenderer.nodeLabel);
    this.graph.nodeThreeObject(GalaxyNodeRenderer.nodeThreeObject);

    this.graph.nodeVisibility(this.nodeVisibility);
    this.graph.linkVisibility(this.linkVisibility);

    this.graph.onNodeClick(this.onNodeClick);
    this.graph.onEngineTick(this.onEngineTick);

    this.graph.nodePositionUpdate(this.nodePositionUpdate);
  }

  private initControls() {
    const controls = this.graph.controls() as OrbitControls;
    controls.enableRotate = true;
    controls.enableZoom = true;
    controls.screenSpacePanning = true;
    controls.update();

    controls.addEventListener('change', () => {
      this.graph.refresh();
    });
  }

  public resetView() {
    this.graph.cameraPosition({ x: 0, y: 0, z: 1000 }, { x: 0, y: 0, z: 0 }, 1500);
  }

  private addStarfield() {
    addSpiralStarfieldLOD(this.graph.scene(), {
      arms: 3,
      turns: 4,
      armSpread: 0.3,
      outerRadius: this.galaxy.params.clusterRadiusMax,
      totalCount: 100000,
      distances: [10000, 5000, 0],
      sizes: [1.3, 1, 0.4],
      additive: true,
      biasToCenterExp: 0.5,
    });
    addStarfieldBackground(this.graph.scene());
  }

  private initBloomRenderer() {
    // Simplified approach: Use standard UnrealBloomPass but with careful material setup
    // The outline spheres are already marked with layer 1 and userData.isBloomObject

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      1.0, // strength - moderate glow for outlines
      0.4, // radius - focused glow
      0.6, // threshold - only bright materials bloom
    );

    // Add the bloom pass to force-graph's composer
    this.graph.postProcessingComposer().addPass(bloomPass);

    // Store reference for potential parameter updates
    this.bloomPass = bloomPass;
  }

  private nodeVisibility = (): boolean => {
    return true;
  };

  private linkVisibility = (): boolean => {
    return false;
  };

  private onNodeClick = (node: NodeObject) => {
    this.selectedNode = node as GalaxyNode;
    const selectedDescendantsArray = this.galaxy.getDescendants(this.selectedNode);
    this.selectedDescendants = new Set(selectedDescendantsArray);
    this.selectedNode.data.planetCount = selectedDescendantsArray.filter(
      (descendant) => descendant.type === GalaxyNodeType.PLANET,
    ).length;
    this.nodeSelected.emit(this.selectedNode);
    this.zoomAndPanToSubtree(node);
  };

  private onEngineTick = () => {};

  zoomAndPanToSubtree(node: NodeObject) {
    const nodeDescendants = this.galaxy.getDescendants(node as GalaxyNode);
    const allNodes = [node, ...nodeDescendants];
    const bbox = this.graph.getGraphBbox((node: NodeObject) => {
      return allNodes.includes(node);
    });

    const cx = (bbox.x[0] + bbox.x[1]) / 2;
    const cy = (bbox.y[0] + bbox.y[1]) / 2;
    const cz = (bbox.z[0] + bbox.z[1]) / 2;
    const w = bbox.x[1] - bbox.x[0],
      h = bbox.y[1] - bbox.y[0];

    const cam = this.graph.camera() as THREE.PerspectiveCamera;
    const fov = (cam.fov * Math.PI) / 180;
    const fitH = h / 2 / Math.tan(fov / 2);
    const fitW = w / 2 / Math.tan(Math.atan(Math.tan(fov / 2) * cam.aspect));
    const dist = Math.max(fitH, fitW) + 40;

    // move along current viewing direction
    const dir = cam.position
      .clone()
      .sub((this.graph.controls() as OrbitControls).target)
      .normalize();
    const pos = new THREE.Vector3(cx, cy, cz).add(dir.multiplyScalar(dist));

    this.graph.cameraPosition(pos, { x: cx, y: cy, z: cz }, 1500);
  }

  private nodePositionUpdate = (obj: THREE.Object3D, coords: { x: number; y: number; z: number }, node: any) => {
    // Skip if object doesn't exist
    if (!obj) return;

    // Update position as normal
    Object.assign(obj.position, coords);

    // Get camera
    const camera = this.graph.camera() as THREE.PerspectiveCamera;

    // Calculate distance from camera to node
    const distance = camera.position.distanceTo(new THREE.Vector3(coords.x, coords.y, coords.z));

    // Calculate scaling factor directly proportional to distance
    // Now farther = bigger, closer = smaller
    let scale = 1;

    // Different scaling behavior based on node type
    if (node.type === GalaxyNodeType.STAR || node.type === GalaxyNodeType.PLANET) {
      // For planets and stars, scale up with distance but with a strict maximum size
      // Minimum scale of 0.3 when very close, max of 1.8 when far away
      scale = Math.min(1.8, Math.max(0.3, Math.sqrt(distance) / 10));
    } else if (node.type === GalaxyNodeType.CLUSTER || node.type === GalaxyNodeType.SPIRAL) {
      // For larger structure nodes, allow more dramatic scaling
      scale = Math.min(0.8, Math.max(0.05, Math.sqrt(distance) / 15));
    }

    // Apply scaling
    obj.scale.set(scale, scale, scale);

    // Counter-scale rings to keep them at original size
    if (node.type === GalaxyNodeType.STAR) {
      obj.traverse((child: THREE.Object3D) => {
        if (child.userData.isRings) {
          // Counter-scale the rings to maintain their original size
          const counterScale = 1 / scale;
          child.scale.set(counterScale, counterScale, counterScale);
        }
      });
    }

    // Return true to override default position update
    return true;
  };
}
